// Agentes: el equipo de IA del negocio. Cada uno es una especificación (oficio, instrucciones, modelo, herramientas,
// horario y topes) con su propia llave interna. Se le encarga algo a mano o trabaja solo por horario; las escrituras
// esperan una aprobación salvo las que se le concedan a propósito.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Cargando, Input, Modal, PageHead, Select, Tag, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { desde, proxima, usd } from '../lib/formato';
import { AGENTES_PLANTILLA, type PlantillaAgente } from '../lib/plantillas';
import { useSesion } from '../lib/sesion';
import { Area, AvisoSoloVer, Casilla, Estado, SoloSiAdministra } from '../componentes/Comunes';
import { DetalleEjecucion, EditorHorario, horarioEnPalabras, invalidarTrabajo } from '../componentes/Trabajo';

interface Form {
  id: string | null; nombre: string; descripcion: string; instrucciones: string; tarea: string; modelo: string; herramientas: string[]; escriturasLibres: string[];
  maxPasos: string; topeEjecucion: string; presupuestoMensual: string; ocultarDatosPersonales: boolean; horario: { hora: string; dias: number[] } | null; estado: 'activo' | 'pausado';
}

const desdePlantilla = (p: PlantillaAgente | null, modelo: string): Form => ({
  id: null, nombre: p?.nombre ?? '', descripcion: p?.descripcion ?? '', instrucciones: p?.instrucciones ?? '', tarea: p?.tarea ?? '', modelo,
  herramientas: [], escriturasLibres: [], maxPasos: '10', topeEjecucion: '0.5', presupuestoMensual: '', ocultarDatosPersonales: false, horario: p?.horario ?? null, estado: 'activo',
});
const alternar = (lista: string[], v: string, si: boolean) => (si ? [...new Set([...lista, v])] : lista.filter((x) => x !== v));

export function Agentes() {
  const { aviso } = useSesion();
  const qc = useQueryClient();
  const agentes = useQuery({ queryKey: ['agentes'], queryFn: () => api<any[]>('/api/agentes'), refetchInterval: 15_000 });
  const modelos = useQuery({ queryKey: ['modelos'], queryFn: () => api<any[]>('/api/modelos') });
  const herramientas = useQuery({ queryKey: ['herramientas'], queryFn: () => api<any[]>('/api/herramientas') });
  const [elegir, setElegir] = useState(false);
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [encargo, setEncargo] = useState<{ agente: any; texto: string } | null>(null);
  const [aprendizajes, setAprendizajes] = useState<any | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);

  const porConector = useMemo(() => {
    const g = new Map<string, { nombre: string; escribe: boolean }[]>();
    for (const t of herramientas.data ?? []) {
      const [c] = (t.name as string).split('__');
      g.set(c!, [...(g.get(c!) ?? []), { nombre: t.name, escribe: !t.annotations?.readOnlyHint }]);
    }
    return [...g.entries()];
  }, [herramientas.data]);

  const guardar = useMutation({
    mutationFn: (f: Form) => api<{ id: string }>(f.id ? `/api/agentes/${f.id}` : '/api/agentes', {
      nombre: f.nombre, descripcion: f.descripcion || null, instrucciones: f.instrucciones, tarea: f.tarea || null, modelo: f.modelo,
      herramientas: f.herramientas, escriturasLibres: f.escriturasLibres, maxPasos: Number(f.maxPasos), topeEjecucion: Number(f.topeEjecucion),
      presupuestoMensual: f.presupuestoMensual === '' ? null : Number(f.presupuestoMensual), ocultarDatosPersonales: f.ocultarDatosPersonales,
      horario: f.horario?.dias.length ? f.horario : null, estado: f.estado,
    }),
    onSuccess: (_r, f) => { invalidarTrabajo(qc); void qc.invalidateQueries({ queryKey: ['llaves'] }); setForm(null); aviso(f.id ? 'Agente cambiado' : 'Agente creado'); },
    onError: (e) => setError(mensajeDeError(e)),
  });
  const estado = useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: string }) => api(`/api/agentes/${id}/estado`, { estado }),
    onSuccess: () => { invalidarTrabajo(qc); aviso('Listo'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const encargar = useMutation({
    mutationFn: (x: { agente: any; texto: string }) => api<{ id: string }>(`/api/agentes/${x.agente.id}/encargar`, { encargo: x.texto.trim() || null }),
    onSuccess: (r) => { invalidarTrabajo(qc); setEncargo(null); setAbierto(r.id); },
    onError: (e) => aviso(mensajeDeError(e)),
  });

  if (agentes.isLoading) return <Cargando />;
  const primerModelo = modelos.data?.find((m) => m.estado === 'activo')?.nombre ?? '';
  const editar = (a: any) => {
    setError(null);
    setForm({
      id: a.id, nombre: a.nombre, descripcion: a.descripcion ?? '', instrucciones: a.instrucciones, tarea: a.tarea ?? '', modelo: a.modelo,
      herramientas: a.herramientas, escriturasLibres: a.escrituras_libres, maxPasos: String(a.max_pasos), topeEjecucion: String(a.tope_ejecucion),
      presupuestoMensual: a.presupuesto_mensual === null ? '' : String(a.presupuesto_mensual), ocultarDatosPersonales: a.ocultar_datos_personales, horario: a.horario, estado: a.estado,
    });
  };
  // Las escrituras que el agente tendría, para conceder a propósito las que no piden aprobación.
  const escriturasConcedidas = (f: Form) => porConector.flatMap(([c, lista]) => lista.filter((h) => h.escribe && (f.herramientas.includes('*') || f.herramientas.includes(c) || f.herramientas.includes(h.nombre))).map((h) => h.nombre));

  return (
    <div className="page">
      <PageHead title="Agentes" subtitle="El equipo de IA del negocio. Cada agente tiene un oficio, sus herramientas y sus topes; trabaja cuando se le encarga o por horario, y pide permiso antes de escribir.">
        <SoloSiAdministra><Button icon="plus" disabled={!modelos.data?.length} onClick={() => setElegir(true)}>Nuevo agente</Button></SoloSiAdministra>
      </PageHead>
      <AvisoSoloVer />
      {modelos.data && !modelos.data.length && <div className="aviso-caja" style={{ marginBottom: 16 }}>Un agente usa un modelo del gateway. Crear uno primero en «Modelos».</div>}

      {!agentes.data?.length ? (
        <Vacio titulo="Todavía no hay agentes">Empezar por un oficio que hoy alguien hace a mano todos los días: el cuadre de caja, la cobranza, el pedido a proveedores.</Vacio>
      ) : (
        <div className="grid-2">
          {agentes.data.map((a) => (
            <div key={a.id} className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10, opacity: a.estado === 'pausado' ? 0.7 : 1 }}>
              <div className="entre">
                <div className="h-card">{a.nombre}</div>
                <div className="fila">{a.por_aprobar > 0 && <Tag tone="amber">{a.por_aprobar} por aprobar</Tag>}<Estado estado={a.estado} /></div>
              </div>
              {a.descripcion && <div className="muted" style={{ fontSize: 14, lineHeight: '20px' }}>{a.descripcion}</div>}
              <div style={{ fontSize: 13, lineHeight: '20px' }}>
                <div><code>{a.modelo}</code> · {a.herramientas.length ? `${a.herramientas.length} permiso${a.herramientas.length === 1 ? '' : 's'} de herramientas` : 'sin herramientas'}{a.escrituras_libres.length ? ` · ${a.escrituras_libres.length} escritura${a.escrituras_libres.length === 1 ? '' : 's'} sin aprobación` : ''}</div>
                <div className="muted">{horarioEnPalabras(a.horario)}{a.proximo_en ? ` · la próxima: ${proxima(a.proximo_en)}` : ''}</div>
                <div className="muted">{usd(a.gasto_mes)} este mes{a.presupuesto_mensual !== null ? ` de ${usd(a.presupuesto_mensual)}` : ''} · {a.ejecuciones_7d} trabajos en 7 días · <button className="enlace" onClick={() => setAprendizajes(a)}>{a.aprendizajes} aprendizaje{a.aprendizajes === 1 ? '' : 's'}</button></div>
              </div>
              {a.ultima && (
                <button className="plantilla" onClick={() => setAbierto(a.ultima.id)} style={{ padding: 10 }}>
                  <span className="entre" style={{ width: '100%' }}><span className="etiqueta">Último trabajo · {desde(a.ultima.en)}</span><Estado estado={a.ultima.estado} /></span>
                  <span>{a.ultima.resultado ?? '…'}</span>
                </button>
              )}
              <SoloSiAdministra>
                <div className="fila" style={{ marginTop: 'auto' }}>
                  <Button size="sm" icon="play" disabled={a.estado !== 'activo'} onClick={() => setEncargo({ agente: a, texto: a.tarea ?? '' })}>Encargar</Button>
                  <Button size="sm" variant="secondary" icon="pencil" onClick={() => editar(a)}>Cambiar</Button>
                  <Button size="sm" variant="ghost" icon={a.estado === 'activo' ? 'pause' : 'play'} onClick={() => estado.mutate({ id: a.id, estado: a.estado === 'activo' ? 'pausado' : 'activo' })}>{a.estado === 'activo' ? 'Pausar' : 'Reactivar'}</Button>
                </div>
              </SoloSiAdministra>
            </div>
          ))}
        </div>
      )}

      {elegir && (
        <Modal title="Nuevo agente" subtitle="Empezar por un oficio. Todo se puede cambiar antes de guardar." onClose={() => setElegir(false)} width={720}>
          <div className="plantillas">
            {AGENTES_PLANTILLA.map((p) => (
              <button key={p.clave} className="plantilla" onClick={() => { setElegir(false); setError(null); setForm(desdePlantilla(p, primerModelo)); }}>
                <b>{p.nombre}</b><span>{p.descripcion}</span>
              </button>
            ))}
            <button className="plantilla" onClick={() => { setElegir(false); setError(null); setForm(desdePlantilla(null, primerModelo)); }}>
              <b>En blanco</b><span>Un oficio que no está en la lista.</span>
            </button>
          </div>
        </Modal>
      )}

      {form && (
        <Modal title={form.id ? `Cambiar «${form.nombre}»` : 'Nuevo agente'} onClose={() => setForm(null)} width={760}
          subtitle="Lo que no se le concede, no existe para el agente. Toda escritura pide aprobación, salvo las que se marquen abajo."
          footer={<>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancelar</Button>
            <Button disabled={guardar.isPending || form.nombre.trim().length < 2 || form.instrucciones.trim().length < 10 || !form.modelo} onClick={() => { setError(null); guardar.mutate(form); }}>{form.id ? 'Guardar' : 'Crear agente'}</Button>
          </>}>
          <div className="grid-2" style={{ gap: 12 }}>
            <Input label="Oficio" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Cobranza" autoFocus />
            <Select label="Modelo" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })}
              options={[...(form.modelo ? [] : [{ value: '', label: 'Elegir…' }]), ...(modelos.data ?? []).map((m) => ({ value: m.nombre as string, label: m.deGlubi ? `${m.nombre} · de Glubi` : m.nombre as string }))]} />
          </div>
          <Input label="Qué hace, en una frase" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
          <Area label="Instrucciones" filas={5} value={form.instrucciones} onChange={(v) => setForm({ ...form, instrucciones: v })} placeholder="Qué revisa, con qué criterio, qué nunca hace." />
          <Area label="Tarea cuando trabaja solo (y encargo por defecto)" filas={2} value={form.tarea} onChange={(v) => setForm({ ...form, tarea: v })} placeholder="Revisar el cuadre de ayer y decir si cuadró." />
          <EditorHorario valor={form.horario} onChange={(horario) => setForm({ ...form, horario })} />

          <div className="stack" style={{ gap: 6 }}>
            <span className="etiqueta">Herramientas</span>
            <div className="casillas" style={{ maxHeight: 260 }}>
              {herramientas.isLoading && <span className="muted" style={{ fontSize: 13 }}>Preguntando a los conectores…</span>}
              {!herramientas.isLoading && !porConector.length && <span className="muted" style={{ fontSize: 13 }}>No hay conectores con herramientas. El agente solo podrá razonar con lo que se le diga.</span>}
              {porConector.map(([conector, lista]) => {
                const entero = form.herramientas.includes(conector);
                return (
                  <div key={conector} className="stack" style={{ gap: 4, paddingTop: 4 }}>
                    <Casilla checked={entero} onChange={(v) => setForm({ ...form, herramientas: alternar(form.herramientas.filter((h) => !h.startsWith(`${conector}__`)), conector, v) })}>
                      <b style={{ fontWeight: 500 }}>Todo «{conector}»</b> <span className="muted">({lista.length})</span>
                    </Casilla>
                    {!entero && lista.map((h) => (
                      <div key={h.nombre} style={{ paddingLeft: 22 }}>
                        <Casilla checked={form.herramientas.includes(h.nombre)} onChange={(v) => setForm({ ...form, herramientas: alternar(form.herramientas, h.nombre, v), escriturasLibres: v ? form.escriturasLibres : form.escriturasLibres.filter((x) => x !== h.nombre) })}>
                          <code>{h.nombre}</code>{h.escribe && <span style={{ color: 'var(--amber-text)', fontSize: 12 }}> · escribe</span>}
                        </Casilla>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
          {escriturasConcedidas(form).length > 0 && (
            <div className="stack" style={{ gap: 6 }}>
              <span className="etiqueta">Escrituras que hace sin pedir aprobación (concederlas a propósito)</span>
              <div className="casillas">
                {escriturasConcedidas(form).map((h) => (
                  <Casilla key={h} checked={form.escriturasLibres.includes(h)} onChange={(v) => setForm({ ...form, escriturasLibres: alternar(form.escriturasLibres, h, v) })}><code>{h}</code></Casilla>
                ))}
              </div>
            </div>
          )}

          <div className="grid-2" style={{ gap: 12 }}>
            <Input label="Tope por trabajo" unit="US$" inputMode="decimal" value={form.topeEjecucion} onChange={(e) => setForm({ ...form, topeEjecucion: e.target.value.replace(',', '.') })} />
            <Input label="Tope al mes (vacío: sin tope)" unit="US$" inputMode="decimal" value={form.presupuestoMensual} onChange={(e) => setForm({ ...form, presupuestoMensual: e.target.value.replace(',', '.') })} />
            <Input label="Vueltas máximas por trabajo" inputMode="numeric" value={form.maxPasos} onChange={(e) => setForm({ ...form, maxPasos: e.target.value.replace(/\D/g, '') })} />
          </div>
          <Casilla checked={form.ocultarDatosPersonales} onChange={(v) => setForm({ ...form, ocultarDatosPersonales: v })}>
            Tapar correos, celulares, tarjetas y documentos antes de mandar un mensaje al proveedor del modelo
          </Casilla>
          {error && <div className="error-caja">{error}</div>}
        </Modal>
      )}

      {encargo && (
        <Modal title={`Encargarle a «${encargo.agente.nombre}»`} subtitle="Corre ya, con sus herramientas y sus topes. Cuesta lo que cuesta y queda en el registro." onClose={() => setEncargo(null)} width={620}
          footer={<>
            <Button variant="ghost" onClick={() => setEncargo(null)}>Cancelar</Button>
            <Button icon="play" disabled={encargar.isPending || !encargo.texto.trim()} onClick={() => encargar.mutate(encargo)}>Encargar</Button>
          </>}>
          <Area label="Qué tiene que hacer" filas={4} value={encargo.texto} onChange={(texto) => setEncargo({ ...encargo, texto })} />
        </Modal>
      )}

      {aprendizajes && <Aprendizajes agente={aprendizajes} onClose={() => setAprendizajes(null)} />}
      {abierto && <DetalleEjecucion id={abierto} onClose={() => setAbierto(null)} />}
    </div>
  );
}

// Lo que el negocio le corrigió al agente. Se retira, no se borra: queda la historia de quién lo dijo.
function Aprendizajes({ agente, onClose }: { agente: any; onClose: () => void }) {
  const { aviso } = useSesion();
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const q = useQuery({ queryKey: ['aprendizajes', agente.id], queryFn: () => api<any[]>(`/api/agentes/${agente.id}/aprendizajes`) });
  const recargar = () => { void qc.invalidateQueries({ queryKey: ['aprendizajes', agente.id] }); invalidarTrabajo(qc); };
  const agregar = useMutation({
    mutationFn: () => api(`/api/agentes/${agente.id}/aprendizajes`, { texto: texto.trim() }),
    onSuccess: () => { setTexto(''); recargar(); aviso('Aprendizaje guardado'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const retirar = useMutation({
    mutationFn: (id: string) => api(`/api/aprendizajes/${id}/retirar`, {}),
    onSuccess: () => { recargar(); aviso('Aprendizaje retirado'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  return (
    <Modal title={`Aprendizajes de «${agente.nombre}»`} subtitle="Correcciones de personas del negocio. El agente las tiene en cuenta en cada trabajo; el modelo nunca escribe aquí." onClose={onClose} width={640}
      footer={<Button onClick={onClose}>Cerrar</Button>}>
      {q.isLoading ? <Cargando /> : (
        <div className="stack" style={{ gap: 8 }}>
          {!q.data?.length && <span className="muted" style={{ fontSize: 14 }}>Todavía no tiene. Se le dejan desde aquí o desde un trabajo que hizo.</span>}
          {q.data?.map((x) => (
            <div key={x.id} className="entre" style={{ borderBottom: '1px solid var(--border-default)', paddingBottom: 8, opacity: x.estado === 'retirado' ? 0.55 : 1 }}>
              <div style={{ fontSize: 14, flex: 1, minWidth: 0 }}>
                <div style={{ textDecoration: x.estado === 'retirado' ? 'line-through' : undefined }}>{x.texto}</div>
                <div className="muted" style={{ fontSize: 12 }}>{x.creado_por?.nombre} · {desde(x.creado_en)}{x.estado === 'retirado' ? ` · retirado por ${x.retirado_por?.nombre}` : ''}</div>
              </div>
              {x.estado === 'activo' && <SoloSiAdministra><Button size="sm" variant="ghost" icon="x" onClick={() => retirar.mutate(x.id)}>Retirar</Button></SoloSiAdministra>}
            </div>
          ))}
          <SoloSiAdministra>
            <Area label="Nuevo aprendizaje" filas={2} value={texto} onChange={setTexto} placeholder="El efectivo de la noche se cuenta al día siguiente." />
            <div><Button size="sm" icon="plus" disabled={texto.trim().length < 3 || agregar.isPending} onClick={() => agregar.mutate()}>Guardar</Button></div>
          </SoloSiAdministra>
        </div>
      )}
    </Modal>
  );
}
